import { Check } from 'lucide-react'

import { Empty } from '@/components/feedback/Empty'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { messages } from '@/lib/messages'
import type { Group, Permission } from '@/types/models'

const text = messages.groups

/**
 * Salt okunur grup × izin matrisi (docs/ADMIN-PANEL-PLAN.md Faz 4): satırda izinler (izin grubuna
 * göre başlıklı), sütunda gruplar. Düzenleme grubun kendi ekranından; başlık o ekranı açar.
 */
export function GroupPermissionMatrix({
  groups,
  permissions,
  onOpenGroup,
}: {
  groups: Group[]
  permissions: Permission[]
  onOpenGroup: (groupId: string) => void
}) {
  if (groups.length === 0) return <Empty />

  const sections = new Map<string, Permission[]>()
  for (const permission of permissions) {
    sections.set(permission.group, [...(sections.get(permission.group) ?? []), permission])
  }
  const granted = new Map(groups.map((group) => [group.id, new Set(group.permission_keys)]))

  return (
    // Geniş matris kendi kabında kayar; sayfa yatay kaymaz (DESIGN §4).
    <div className="border-border overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="bg-surface sticky left-0 z-1 min-w-56">
              {text.matrixPermission}
            </TableHead>
            {groups.map((group) => (
              <TableHead key={group.id} className="text-center whitespace-nowrap">
                <button
                  type="button"
                  className="hover:text-foreground focus-visible:ring-ring rounded-sm underline-offset-4 outline-hidden hover:underline focus-visible:ring-2"
                  title={text.matrixOpenGroup}
                  onClick={() => onOpenGroup(group.id)}
                >
                  {group.name}
                </button>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...sections.entries()].map(([section, items]) => [
            <TableRow key={`section-${section}`} className="hover:bg-transparent">
              {/* Hücre satır boyu genişler; metin ayrı yapışkan öğede ki yana kayınca görünür kalsın. */}
              <TableCell colSpan={groups.length + 1} className="bg-surface-muted p-0">
                <span className="text-text-secondary sticky left-0 inline-block px-2 py-1 text-xs font-medium">
                  {section}
                </span>
              </TableCell>
            </TableRow>,
            ...items.map((permission) => (
              <TableRow key={permission.key}>
                <TableCell className="bg-surface sticky left-0 z-1" title={permission.key}>
                  {permission.label}
                </TableCell>
                {groups.map((group) => {
                  const has = granted.get(group.id)?.has(permission.key) ?? false
                  return (
                    <TableCell
                      key={group.id}
                      className="text-center"
                      aria-label={`${group.name}: ${has ? text.matrixGranted : text.matrixNotGranted}`}
                    >
                      {has && <Check aria-hidden="true" className="text-success mx-auto size-4" />}
                    </TableCell>
                  )
                })}
              </TableRow>
            )),
          ])}
        </TableBody>
      </Table>
    </div>
  )
}
